/**
 * **The gate everything the AI generates has to pass through.**
 *
 * This repo has two real validators, they live in TypeScript, and they're
 * distinct from each other — this is what the plan statement corrects:
 *
 * | What gets validated | With what | Where |
 * |---|---|---|
 * | A **block's definition** (`quiz`, `flashcard`, …) | `BlockPlugin.validate(def)` | `learning-blocks/types.ts` |
 * | A **module's config** (`lesson`, `resource`, `assignment`, …) | `CourseModulePlugin.configSchema.parse(config)` | `academy/modules/types.ts` |
 *
 * They aren't the same and don't live in the same registry: blocks **don't**
 * have `configSchema`. You have to go through both, each in its own, and
 * that's what [`validateProposalItem`] does.
 *
 * Why here and not in Rust: the generator is Rust, but reimplementing these
 * validators there would create a **second source of truth** that silently
 * drifts out of sync the first time someone registers a new plugin, and the
 * block engine would stop being extensible. The server puts up a cheap
 * structural net (known type, fields present, ids that exist in the course)
 * and leaves blocks at `valid: false` on purpose: without this file, nothing gets approved.
 */

import { validateBlock } from "../../learning-blocks/engine";
import type { BlockRegistry } from "../../learning-blocks/registry";
import type { CourseModuleRegistry } from "../modules/registry";
import { isPlainObject } from "../modules/types";
import type { AiProposalItem, AiProposalKind } from "./contract";

/** Verdict on an item. `valid: false` ⇒ cannot be approved. */
export interface ItemVerdict {
  valid: boolean;
  errors: string[];
}

export interface ValidationContext {
  blocks: BlockRegistry;
  modules: CourseModuleRegistry;
  /**
   * Kinds enabled in THIS course (`registry.enabledKinds(...)`). A kind
   * that's registered but turned off in `course_settings.enabled_module_kinds`
   * can't be approved: generating with AI isn't a permit to skip a setting.
   */
  enabledModuleKinds: readonly string[];
}

function str(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Validates a block item against `BlockPlugin.validate`.
 *
 * Deliberate note: it's the `def` that's validated, **not** with
 * `configSchema`. A block doesn't have `configSchema`; confusing them is the
 * mistake this file exists to avoid.
 */
function validateBlockItem(item: AiProposalItem, ctx: ValidationContext): ItemVerdict {
  const { payload } = item;
  const type = str(payload.type);
  if (!type) {
    return { valid: false, errors: ["el bloque propuesto no declara su tipo"] };
  }
  if (!ctx.blocks.has(type)) {
    return { valid: false, errors: [`tipo de bloque desconocido: «${type}»`] };
  }
  const { def } = payload;
  if (!isPlainObject(def)) {
    return { valid: false, errors: ["la definición del bloque no es un objeto"] };
  }
  const result = validateBlock(ctx.blocks, { id: item.id, type, def });
  return result.ok ? { valid: true, errors: [] } : { valid: false, errors: result.errors };
}

/**
 * Validates a module item against `CourseModulePlugin.configSchema` — the
 * OTHER registry — and against the kinds actually enabled in the course.
 */
function validateModuleItem(item: AiProposalItem, ctx: ValidationContext): ItemVerdict {
  const { payload } = item;
  const kind = str(payload.kind);
  const errors: string[] = [];
  if (!kind) {
    return { valid: false, errors: ["el módulo propuesto no declara su tipo"] };
  }
  const plugin = ctx.modules.get(kind);
  if (!plugin) {
    return { valid: false, errors: [`tipo de módulo desconocido: «${kind}»`] };
  }
  if (!ctx.enabledModuleKinds.includes(kind)) {
    errors.push(`«${kind}» no está habilitado en este curso`);
  }
  if (!str(payload.title)) {
    errors.push("el módulo propuesto no tiene título");
  }
  const parsed = plugin.configSchema.parse(payload.config ?? {});
  if (!parsed.ok) {
    errors.push(...parsed.errors);
  }
  return { valid: errors.length === 0, errors };
}

function validateUnitItem(item: AiProposalItem): ItemVerdict {
  return str(item.payload.title)
    ? { valid: true, errors: [] }
    : { valid: false, errors: ["la unidad propuesta no tiene título"] };
}

function validateCourseItem(item: AiProposalItem): ItemVerdict {
  return str(item.payload.title)
    ? { valid: true, errors: [] }
    : { valid: false, errors: ["el curso propuesto no tiene título"] };
}

/**
 * A generated rubric: observable criteria with a name and description. It's
 * the shape consumed by `render_rubric` on the server; if it changes there, it changes here.
 */
function validateRubricItem(item: AiProposalItem): ItemVerdict {
  const { criteria } = item.payload;
  if (!Array.isArray(criteria) || criteria.length === 0) {
    return { valid: false, errors: ["la rúbrica no tiene criterios"] };
  }
  const errors: string[] = [];
  criteria.forEach((c, i) => {
    if (!isPlainObject(c)) {
      errors.push(`el criterio ${i + 1} no es un objeto`);
      return;
    }
    if (!str(c.name)) {
      errors.push(`el criterio ${i + 1} no tiene nombre`);
    }
    if (!str(c.description)) {
      errors.push(`el criterio «${str(c.name) || i + 1}» no dice qué hay que ver en la entrega`);
    }
  });
  return { valid: errors.length === 0, errors };
}

/**
 * Verdict on ONE item, crossing it against the registry it belongs to.
 *
 * Rule that isn't negotiable: **a delete never self-validates**. It's the
 * operation that's most expensive to get wrong and a person always approves it by hand.
 */
export function validateProposalItem(item: AiProposalItem, ctx: ValidationContext): ItemVerdict {
  if (item.op === "delete") {
    return {
      valid: false,
      errors: ["un borrado propuesto por la IA lo confirma una persona, siempre"],
    };
  }
  switch (item.entity) {
    case "block": {
      return validateBlockItem(item, ctx);
    }
    case "course_module": {
      return validateModuleItem(item, ctx);
    }
    case "unit": {
      return validateUnitItem(item);
    }
    case "course": {
      return validateCourseItem(item);
    }
    case "rubric": {
      return validateRubricItem(item);
    }
    default: {
      // A type this file doesn't know how to judge does NOT get approved on
      // its own. Returning `true` by default would be the feature's most expensive silent failure.
      return {
        valid: false,
        errors: [`«${item.entity}» necesita revisión manual antes de aplicarse`],
      };
    }
  }
}

/** Verdict of all items, indexed by id. */
export function validateProposalItems(
  items: readonly AiProposalItem[],
  ctx: ValidationContext,
): Map<string, ItemVerdict> {
  return new Map(items.map((i) => [i.id, validateProposalItem(i, ctx)]));
}

/** Summary for the review panel header. */
export interface ProposalReviewSummary {
  total: number;
  valid: number;
  invalid: number;
  applied: number;
  rejected: number;
  /** Is there anything left that can be approved? */
  approvable: number;
}

export function summarizeReview(
  items: readonly AiProposalItem[],
  verdicts: ReadonlyMap<string, ItemVerdict>,
): ProposalReviewSummary {
  let valid = 0;
  let approvable = 0;
  let applied = 0;
  let rejected = 0;
  for (const item of items) {
    const v = verdicts.get(item.id);
    if (v?.valid) {
      valid += 1;
      if (item.status === "pending" || item.status === "approved") {
        approvable += 1;
      }
    }
    if (item.status === "applied") {
      applied += 1;
    }
    if (item.status === "rejected") {
      rejected += 1;
    }
  }
  return {
    total: items.length,
    valid,
    invalid: items.length - valid,
    applied,
    rejected,
    approvable,
  };
}

/** Items of a proposal, in the order they must be applied. */
const APPLY_ORDER: readonly string[] = ["course", "unit", "lesson", "course_module", "block"];

/**
 * Sorts items by dependency: the unit before its module, the module before
 * its block. Applying them in the order the model returned them would leave
 * orphaned children pointing at parents that don't exist yet.
 */
export function sortForApply(items: readonly AiProposalItem[]): AiProposalItem[] {
  const rank = (e: string) => {
    const i = APPLY_ORDER.indexOf(e);
    return i === -1 ? APPLY_ORDER.length : i;
  };
  return items.toSorted((a, b) => rank(a.entity) - rank(b.entity) || a.path.localeCompare(b.path));
}

/** Can this class of proposal be applied to a course tree? */
export function isApplicableKind(kind: AiProposalKind): boolean {
  return kind === "structure" || kind === "blocks" || kind === "edit";
}
